import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import '../FormConfiguration/FormConfiguration.css';
import mainLogo from '../../../../assets/global_assets/assignClients/mainIcon.png';
import { getAllUnits } from '../../../../services/global_redux/globale_actions/Unit.action';
import {
  fetchEmployeesByClientAll,
  postEmployeeTransfer,
  postEmployeeTransferRevert,
  uploadEmployeeBulkTransfer,
  downloadEmployeeBulkTransferTemplate,
} from '../../../../services/register_redux/register_api_services/Employee.api';
import { globalApi } from '../../../../services/api';

function parseEmployeeList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function EmployeeBranchTransfer({ showSnackbar, setLoading }) {
  const dispatch = useDispatch();
  /** Parent often passes a new showSnackbar each render; keep a stable ref so loadEmployees does not retrigger in a loop. */
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;
  /** At most one branch-mismatch toast per (org + source branch) until the user changes that selection. */
  const branchMismatchToastKeyRef = useRef(null);

  const normalize = (value) => String(value ?? '').trim().toUpperCase();
  const [unitDetails, setUnitDetails] = useState([]);
  const [currentMonthStr, setCurrentMonthStr] = useState('');
  const [employees, setEmployees] = useState([]);

  const [sourceClientId, setSourceClientId] = useState('');
  const [sourceUnitId, setSourceUnitId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [toClientId, setToClientId] = useState('');
  const [toUnitId, setToUnitId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');

  const [bulkFile, setBulkFile] = useState(null);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  /** When branch-specific API returns no rows but org has employees, we load all org rows and show them. */
  const [showAllEmployeesDespiteBranch, setShowAllEmployeesDespiteBranch] = useState(false);
  const [showRevertPopup, setShowRevertPopup] = useState(false);

  const clientOptions = useMemo(() => {
    const m = new Map();
    unitDetails.forEach((u) => {
      const cid = String(u.client_id ?? '').trim();
      if (cid && !m.has(cid)) {
        m.set(cid, u.client_name || cid);
      }
    });
    return Array.from(m.entries()).map(([id, name]) => ({ client_id: id, client_name: name }));
  }, [unitDetails]);

  const targetUnits = useMemo(
    () => unitDetails.filter((u) => normalize(u.client_id) === normalize(toClientId)),
    [unitDetails, toClientId]
  );

  const sourceUnits = useMemo(
    () => unitDetails.filter((u) => normalize(u.client_id) === normalize(sourceClientId)),
    [unitDetails, sourceClientId]
  );

  const filteredEmployees = useMemo(() => {
    if (!sourceUnitId || showAllEmployeesDespiteBranch) return employees;
    return employees.filter((e) => normalize(e.unit_id) === normalize(sourceUnitId));
  }, [employees, sourceUnitId, showAllEmployeesDespiteBranch]);

  const loadEmployees = useCallback(async () => {
    if (!sourceClientId) return;
    setIsDropdownLoading(true);
    try {
      setShowAllEmployeesDespiteBranch(false);
      const unitParam = sourceUnitId ? String(sourceUnitId).trim() : null;
      let data = await fetchEmployeesByClientAll(sourceClientId, unitParam);
      let list = parseEmployeeList(data);
      if (unitParam && list.length === 0) {
        const allData = await fetchEmployeesByClientAll(sourceClientId, null);
        const allList = parseEmployeeList(allData);
        if (allList.length > 0) {
          const toastKey = `${sourceClientId}|${unitParam}`;
          if (branchMismatchToastKeyRef.current !== toastKey) {
            branchMismatchToastKeyRef.current = toastKey;
            showSnackbarRef.current(
              'No employees matched the selected branch. Showing all employees for this organization — verify branch IDs in Employee Master if needed.',
              'warning'
            );
          }
          list = allList;
          setShowAllEmployeesDespiteBranch(true);
        }
      }
      setEmployees(list);
    } catch (e) {
      console.error(e);
      setEmployees([]);
      const msg = typeof e === 'string' ? e : e?.message || 'Could not load employees for this month';
      showSnackbarRef.current(msg, 'error');
    } finally {
      setIsDropdownLoading(false);
    }
  }, [sourceClientId, sourceUnitId]);

  useEffect(() => {
    const init = async () => {
      let unitsResponse = null;
      try {
        unitsResponse = await dispatch(getAllUnits());
        if (unitsResponse.success && unitsResponse.data?.unit_details) {
          setUnitDetails(unitsResponse.data.unit_details);
        }
      } catch (err) {
        console.error(err);
      }
      const selectedClient = sessionStorage.getItem('selectedClient');
      if (selectedClient && selectedClient !== 'null') {
        setSourceClientId(selectedClient);
        setToClientId(selectedClient);
      } else if (
        unitsResponse?.success &&
        Array.isArray(unitsResponse.data?.unit_details) &&
        unitsResponse.data.unit_details.length > 0
      ) {
        const firstClient = String(unitsResponse.data.unit_details[0].client_id);
        setSourceClientId(firstClient);
        setToClientId(firstClient);
      }
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    const fetchCurrentMonthForSource = async () => {
      if (!sourceClientId) return;
      setIsDropdownLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const res = await globalApi.get(`/get_current_month/${sourceClientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const monthVal = Array.isArray(res.data) && res.data[0]?.current_month ? res.data[0].current_month : '';
        if (monthVal) {
          setCurrentMonthStr(monthVal);
        } else {
          const fallback = new Date().toISOString().slice(0, 10).replace(/-\d{2}$/, '-01');
          setCurrentMonthStr(fallback);
          showSnackbarRef.current(
            'Current month not configured for this organization. Using this month as fallback.',
            'warning'
          );
        }
      } catch (err) {
        console.error(err);
        const fallback = new Date().toISOString().slice(0, 10).replace(/-\d{2}$/, '-01');
        setCurrentMonthStr(fallback);
        showSnackbarRef.current(
          'Could not load current month for selected organization. Using this month as fallback.',
          'warning'
        );
      } finally {
        setIsDropdownLoading(false);
      }
    };
    fetchCurrentMonthForSource();
  }, [sourceClientId]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    setCurrentMonthStr('');
    setEmployees([]);
    setEmployeeId('');
    setSourceUnitId('');
    setToUnitId('');
    setToClientId(sourceClientId || '');
    setShowAllEmployeesDespiteBranch(false);
    branchMismatchToastKeyRef.current = null;
  }, [sourceClientId]);

  useEffect(() => {
    setEmployeeId('');
    setShowAllEmployeesDespiteBranch(false);
    branchMismatchToastKeyRef.current = null;
  }, [sourceUnitId]);

  const selectedEmployee = useMemo(
    () =>
      filteredEmployees.find((e) => String(e.id) === String(employeeId)) ||
      employees.find((e) => String(e.id) === String(employeeId)),
    [filteredEmployees, employees, employeeId]
  );

  const handleSingleTransfer = async (e) => {
    e.preventDefault();
    if (!sourceClientId) {
      showSnackbar('Choose source organization first', 'warning');
      return;
    }
    if (!employeeId || !toUnitId) {
      showSnackbar('Choose an employee and target branch', 'warning');
      return;
    }
    if (!effectiveDate.trim()) {
      showSnackbar('Transfer effective date is required (payroll registers use the first day of that month)', 'warning');
      return;
    }
    if (
      selectedEmployee &&
      normalize(selectedEmployee.unit_id) === normalize(toUnitId) &&
      normalize(selectedEmployee.client_id) === normalize(toClientId || sourceClientId)
    ) {
      showSnackbar('Employee is already on this branch (and organization)', 'warning');
      return;
    }

    const body = {
      client_id: sourceClientId,
      employee_id: Number(employeeId),
      to_unit_id: toUnitId,
      transfer_effective_from: effectiveDate.trim(),
      transfer_reason: reason.trim() || undefined,
    };
    if (toClientId && String(toClientId) !== String(sourceClientId)) {
      body.to_client_id = toClientId;
    }

    setLoading(true);
    try {
      const res = await postEmployeeTransfer(body);
      showSnackbar(res?.message || 'Transfer completed', 'success');
      setEmployeeId('');
      setToUnitId('');
      setReason('');
      setEffectiveDate('');
      loadEmployees();
    } catch (err) {
      const msg = err?.error || err?.message || (typeof err === 'string' ? err : 'Transfer failed');
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevertTransfer = () => {
    if (!employeeId) {
      showSnackbar('Select an employee to revert their last transfer', 'warning');
      return;
    }
    setShowRevertPopup(true);
  };

  const closeRevertPopup = () => {
    setShowRevertPopup(false);
  };

  const confirmRevertTransfer = async () => {
    setShowRevertPopup(false);
    setLoading(true);
    try {
      const res = await postEmployeeTransferRevert({ employee_id: Number(employeeId) });
      showSnackbar(res?.message || 'Transfer reverted', 'success');
      setToUnitId('');
      setReason('');
      setEffectiveDate('');
      loadEmployees();
    } catch (err) {
      const msg = err?.error || err?.message || (typeof err === 'string' ? err : 'Revert failed');
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      const response = await downloadEmployeeBulkTransferTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_bulk_transfer_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      showSnackbar('Template downloaded', 'success');
    } catch (err) {
      showSnackbar(typeof err === 'string' ? err : 'Download failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!sourceClientId || !currentMonthStr) {
      showSnackbar('Organization and current month are required', 'warning');
      return;
    }
    if (!bulkFile) {
      showSnackbar('Choose an Excel file first', 'warning');
      return;
    }
    setLoading(true);
    try {
      const res = await uploadEmployeeBulkTransfer(bulkFile, sourceClientId, currentMonthStr);
      const extra =
        res?.errors?.length > 0
          ? ` First errors: ${res.errors
              .slice(0, 3)
              .map((x) => `row ${x.row} (${x.emp_code}): ${x.error}`)
              .join('; ')}`
          : '';
      showSnackbar((res?.message || 'Bulk transfer done') + extra, res?.error_count > 0 ? 'warning' : 'success');
      setBulkFile(null);
      loadEmployees();
    } catch (err) {
      const msg = err?.error || err?.message || (typeof err === 'string' ? err : 'Bulk upload failed');
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contaner-fluid m-0">
      <div className="col-md-12 assign-permission-main-container">
        <div className="row assign-permission-table-container">
          <div className="form-assignment-sub-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="title-header d-flex align-items-center gap-2">
              <div className="logoicon">
                <img src={mainLogo} alt="" />
              </div>
              <h5 className="mb-0">Employee Branch Transfer</h5>
            </div>
            <div className="text-muted small">
              Organization: <strong>{sourceClientId || '-'}</strong>
              {currentMonthStr ? (
                <>
                  {' '}
                  · Month: <strong>{currentMonthStr}</strong>
                </>
              ) : null}
            </div>
          </div>

          <div className="p-4">
            <p className="text-muted small mb-4">
              Move employees between branches (and optionally between organizations you have access to). Each transfer
              needs an effective date; payroll-related registers for that calendar month are updated to the target
              branch.               You can revert a mistaken transfer for the selected employee if their current branch matches the
              last transfer record. After a cross-organization transfer, choose that employee&apos;s{' '}
              <em>current</em> organization in Source so they appear in the list.
            </p>
            {isDropdownLoading && (
              <p className="text-muted small mb-3">Loading organization data and employee list...</p>
            )}

            <h6 className="fw-semibold mb-3">Single transfer</h6>
            <form onSubmit={handleSingleTransfer} className="row g-3 mb-5">
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Source organization</label>
                <select
                  className="form-select form-select-sm"
                  value={sourceClientId}
                  onChange={(ev) => setSourceClientId(ev.target.value)}
                  required
                >
                  <option value="">Select source organization</option>
                  {clientOptions.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.client_name} ({c.client_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Source branch (optional filter)</label>
                <select
                  className="form-select form-select-sm"
                  value={sourceUnitId}
                  onChange={(ev) => setSourceUnitId(ev.target.value)}
                >
                  <option value="">All source branches</option>
                  {sourceUnits.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>
                      {u.unit_name || u.unit_id} ({u.unit_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Employee</label>
                <select
                  className="form-select form-select-sm"
                  value={employeeId}
                  onChange={(ev) => setEmployeeId(ev.target.value)}
                  required
                >
                  <option value="">Select employee</option>
                  {filteredEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} — {emp.emp_name} (branch {emp.unit_id})
                    </option>
                  ))}
                </select>
                {sourceUnitId && !isDropdownLoading && employees.length > 0 && filteredEmployees.length === 0 ? (
                  <div className="form-text text-warning small">
                    No employees found for this branch. Try &quot;All source branches&quot; or verify branch
                    IDs in Employee Master match Unit Master.
                  </div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Target organization</label>
                <select
                  className="form-select form-select-sm"
                  value={toClientId}
                  onChange={(ev) => {
                    setToClientId(ev.target.value);
                    setToUnitId('');
                  }}
                >
                  {clientOptions.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.client_name} ({c.client_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Target branch</label>
                <select
                  className="form-select form-select-sm"
                  value={toUnitId}
                  onChange={(ev) => setToUnitId(ev.target.value)}
                  required
                >
                  <option value="">Select branch</option>
                  {targetUnits.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>
                      {u.unit_name || u.unit_id} ({u.unit_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Effective from (required)</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={effectiveDate}
                  onChange={(ev) => setEffectiveDate(ev.target.value)}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Reason (optional)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="e.g. internal restructuring"
                />
              </div>
              <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
                <button type="submit" className="btn btn-primary btn-sm px-4">
                  Transfer employee
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm px-3"
                  onClick={handleRevertTransfer}
                  disabled={!employeeId}
                  title="Undoes the latest transfer for this employee when their current branch matches history"
                >
                  Revert last transfer
                </button>
              </div>
            </form>

            <hr />
            <h6 className="fw-semibold mb-3">Bulk transfer (Excel)</h6>
            <p className="text-muted small">
              Row 1 is skipped (instructions). Row 2 must be headers: <code>Employee Code</code>,{' '}
              <code>To Branch ID</code>, <code>To Organization ID</code> (optional),{' '}
              <code>Transfer Effective From</code> (required per row), <code>Transfer Reason</code>. Data starts on row 3.
            </p>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleDownloadTemplate}>
                Download template
              </button>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="form-control form-control-sm"
                style={{ maxWidth: 280 }}
                onChange={(ev) => setBulkFile(ev.target.files?.[0] || null)}
              />
              <button type="button" className="btn btn-primary btn-sm" onClick={handleBulkUpload}>
                Upload bulk transfer
              </button>
            </div>
          </div>
        </div>
      </div>
      {showRevertPopup && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}
          onClick={closeRevertPopup}
        >
          <div
            className="shadow"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              border: '1px solid #d9d9d9',
              maxWidth: '460px',
              width: '100%',
              margin: '0 16px',
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="px-3 py-2 border-bottom">
              <h6 className="mb-0 fw-semibold">Revert last transfer?</h6>
            </div>
            <div className="px-3 py-3">
              <p className="mb-0 small">
                Revert the last branch transfer for this employee? Payroll rows for the effective month will move back
                to the previous branch.
              </p>
            </div>
            <div className="d-flex justify-content-end gap-2 px-3 py-2 border-top">
              <button type="button" className="btn btn-outline-secondary btn-sm px-3" onClick={closeRevertPopup}>
                No
              </button>
              <button type="button" className="btn btn-warning btn-sm px-3" onClick={confirmRevertTransfer}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeBranchTransfer;
